/**
 * pt-BR text for audit_log rows (Equipe → Registro de auditoria, Auditoria). Every action the staff
 * API writes (apps/api/src/routes/admin) has a line here; unknown ones fall back to their name.
 */
import { typeLabel } from "./trip-types.ts";

export type AuditRow = { action: string; entity: string; entityId: string | null; data: Record<string, unknown> };

const brl = (cents: unknown) => (typeof cents === "number" ? (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—");
const n = (v: unknown) => (typeof v === "number" ? v.toLocaleString("pt-BR") : "");
const why = (d: Record<string, unknown>) => (typeof d.reason === "string" && d.reason ? `: ${d.reason}` : "");

/** Fixed labels; actions whose text depends on the row are in `auditText`. */
export const AUDIT_ACTIONS: Record<string, string> = {
  "staff.invite": "Convidou um membro",
  "staff.update": "Alterou função ou cidades",
  "staff.deactivate": "Desativou um membro",
  "staff.reactivate": "Reativou um membro",
  "staff.sign_in": "Entrou no admin",
  "staff.2fa_failed": "Errou o código 2FA",
  "driver.review": "Decidiu um cadastro",
  "driver.view": "Abriu um cadastro de motorista",
  "driver.view_live": "Abriu um motorista no ao vivo",
  "driver.search": "Buscou motoristas",
  "driver.suspend": "Suspendeu um motorista",
  "driver.reactivate": "Reativou um motorista",
  "driver.force_offline": "Deixou um motorista offline",
  "driver.notify": "Notificou um motorista",
  "driver.export": "Exportou motoristas (CSV)",
  "driver.request_renewal": "Pediu renovação de documentos",
  "document.view": "Abriu um documento de motorista",
  "pii.reveal": "Viu dados pessoais completos",
  "rider.view": "Abriu um perfil de passageiro",
  "rider.search": "Buscou passageiros",
  "rider.block": "Bloqueou um passageiro",
  "rider.unblock": "Desbloqueou um passageiro",
  "rider.notify": "Notificou um passageiro",
  "rider.export": "Exportou passageiros (CSV)",
  "trip.view": "Abriu o detalhe de uma corrida",
  "trip.search": "Buscou corridas",
  "trip.cancel": "Cancelou uma corrida",
  "trip.refund": "Reembolsou uma corrida",
  "trip.export": "Exportou corridas (CSV)",
  "trip.chat_view": "Leu a conversa de uma corrida",
  "support.view": "Abriu um atendimento",
  "support.assign": "Assumiu um atendimento",
  "support.transfer": "Transferiu um atendimento",
  "support.close": "Encerrou um atendimento",
  "support.refund_offer": "Ofereceu reembolso no suporte",
  "payout_batch.prepare": "Preparou o lote de repasses",
  "payout_batch.approve": "Aprovou o lote de repasses",
  "payout_batch.pause": "Pausou o lote de repasses",
  "payout_batch.resume": "Retomou o lote de repasses",
  "payout_batch.export": "Exportou comprovantes de um lote",
  "payout_item.retry": "Reprocessou repasses",
  "payout_item.hold": "Reteve um repasse",
  "payout_item.release": "Liberou um repasse retido",
  "finance.export": "Exportou lançamentos contábeis",
  "config.update": "Alterou uma configuração",
  "config.reset": "Restaurou uma configuração",
  "fare.update": "Alterou uma tarifa",
  "surge.create": "Criou uma zona de dinâmica",
  "surge.end": "Encerrou uma zona de dinâmica",
  "notification.broadcast": "Enviou uma notificação em massa",
};

/** Action prefixes for the "Área" filter. */
export const AUDIT_AREAS: [string, string][] = [
  ["trip.", "Corridas"], ["driver.", "Motoristas"], ["document.", "Documentos"], ["rider.", "Passageiros"], ["pii.", "Dados pessoais"],
  ["support.", "Suporte"], ["payout_", "Repasses"], ["finance.", "Financeiro"], ["fare.", "Tarifas"], ["surge.", "Dinâmica"],
  ["config.", "Configurações"], ["notification.", "Notificações"], ["staff.", "Equipe e acesso"],
];

export const AUDIT_ENTITIES: Record<string, string> = {
  trip: "Corrida", rider: "Passageiro", driver_profile: "Motorista", driver_document: "Documento", support_ticket: "Atendimento",
  payout_batch: "Lote de repasses", payout_item: "Repasse", ledger: "Lançamentos", config: "Configuração", fare_table: "Tarifa",
  surge_zone: "Zona de dinâmica", notification: "Notificação", staff_member: "Membro da equipe", user: "Acesso",
};

/** "convidou helena@… como Suporte", "reembolsou R$ 6,40 de uma corrida", … (after the actor's name). */
export function auditText(a: AuditRow, roles: Map<string, string>) {
  const d = a.data;
  switch (a.action) {
    case "staff.invite": return `convidou ${d.email} como ${roles.get(String(d.roleId)) ?? d.roleId}`;
    case "staff.update": return "alterou a função ou as cidades de um membro";
    case "staff.deactivate": return "desativou um membro";
    case "staff.reactivate": return "reativou um membro";
    case "staff.sign_in": return d.enrolled ? "ativou o 2FA e entrou" : "entrou no admin";
    case "staff.2fa_failed": return "errou o código 2FA";
    case "driver.review": return { approve: "aprovou um cadastro", resubmit: "pediu reenvio de documentos", reject: "reprovou um cadastro" }[String(d.decision)] ?? "decidiu um cadastro";
    case "driver.view": return "abriu um cadastro de motorista";
    case "driver.view_live": return "abriu um motorista no ao vivo";
    case "driver.search": return `buscou motoristas por "${d.q}"`;
    case "driver.suspend": return `suspendeu um motorista${why(d)}`;
    case "driver.reactivate": return "reativou um motorista";
    case "driver.force_offline": return "deixou um motorista offline";
    case "driver.notify": return `notificou um motorista: "${d.title}"`;
    case "driver.export": return `exportou ${n(d.count)} motoristas em CSV`;
    case "driver.request_renewal": return `pediu a renovação de ${Array.isArray(d.documents) && d.documents.length > 1 ? `${d.documents.length} documentos` : "um documento"} de um motorista`;
    case "document.view": return "abriu um documento de motorista";
    case "pii.reveal": return a.entity === "rider" ? "viu os dados completos de um passageiro" : "viu os dados completos de um motorista";
    case "rider.view": return "abriu um perfil de passageiro";
    case "rider.search": return `buscou passageiros por "${d.q}"`;
    case "rider.block": return `bloqueou um passageiro${typeof d.days === "number" ? ` por ${d.days} dia${d.days > 1 ? "s" : ""}` : " sem prazo"}${why(d)}`;
    case "rider.unblock": return "desbloqueou um passageiro";
    case "rider.notify": return `notificou um passageiro: "${d.title}"`;
    case "rider.export": return `exportou ${n(d.count)} passageiros em CSV`;
    case "trip.view": return "abriu o detalhe de uma corrida";
    case "trip.search": return `buscou corridas por "${d.q}"`;
    case "trip.cancel": return `cancelou uma corrida${why(d)}`;
    case "trip.refund": return `reembolsou ${brl(d.amountCents)} de uma corrida${why(d)}`;
    case "trip.export": return `exportou ${n(d.count)} corridas em CSV`;
    case "trip.chat_view": return "leu a conversa de uma corrida";
    case "support.view": return "abriu um atendimento do suporte";
    case "support.assign": return "assumiu um atendimento";
    case "support.transfer": return `transferiu um atendimento${d.toName ? ` para ${d.toName}` : ""}`;
    case "support.close": return "encerrou um atendimento";
    case "support.refund_offer": return `ofereceu reembolso de ${brl(d.amountCents)}`;
    case "payout_batch.prepare": return "preparou o lote semanal de repasses";
    case "payout_batch.approve": return `aprovou o lote de repasses (${brl(d.totalCents)})`;
    case "payout_batch.pause": return "pausou o lote de repasses";
    case "payout_batch.resume": return "retomou o lote de repasses";
    case "payout_batch.export": return "exportou os comprovantes de um lote";
    case "payout_item.retry": return `reprocessou ${Array.isArray(d.ids) ? d.ids.length : ""} repasse(s)`.replace("  ", " ");
    case "payout_item.hold": return `reteve um repasse${why(d)}`;
    case "payout_item.release": return "liberou um repasse retido";
    case "finance.export": return "exportou os lançamentos para a contabilidade";
    case "config.update": return d && "payout.weekly_day" in d ? "alterou o calendário de repasses" : `alterou a configuração ${a.entityId ?? ""}`.trim();
    case "config.reset": return `voltou ${a.entityId ?? "uma configuração"} ao padrão`;
    case "fare.update": return `alterou a tarifa ${d.category ? typeLabel(String(d.category)) : ""}`.trim();
    case "trip_type.create": return `criou o tipo de corrida ${typeLabel(a.entityId)}`;
    case "trip_type.update": return d && d.active === false ? `desativou o tipo de corrida ${typeLabel(a.entityId)}` : `alterou o tipo de corrida ${typeLabel(a.entityId)}`;
    case "trip_type.icon": return `trocou o ícone de ${typeLabel(a.entityId)}`;
    case "trip_type.icon_removed": return `removeu o ícone de ${typeLabel(a.entityId)}`;
    case "trip_type.order": return "reordenou os tipos de corrida";
    case "trip_type_group.create": return `criou o grupo ${String(d.name ?? a.entityId ?? "")}`.trim();
    case "trip_type_group.update": return `alterou o grupo ${a.entityId ?? ""}`.trim();
    case "trip_type_group.delete": return `excluiu o grupo ${a.entityId ?? ""}`.trim();
    case "driver.trip_types": return `liberou tipos de corrida para o veículo ${String(d.plate ?? "")}: ${Array.isArray(d.to) && d.to.length ? d.to.map((x) => typeLabel(String(x))).join(", ") : "nenhum"}`;
    case "driver.gender": return d.confirmed ? "confirmou o gênero do motorista" : "alterou o gênero do motorista";
    case "surge.create": return `criou uma zona de dinâmica ${typeof d.multiplier === "number" ? `${d.multiplier.toLocaleString("pt-BR")}×` : ""}${typeof d.minutes === "number" ? ` por ${d.minutes} min` : ""}`.trim();
    case "surge.end": return "encerrou uma zona de dinâmica";
    case "notification.broadcast": return `enviou "${d.title}" para ${n(d.recipients)} ${d.audience === "riders" ? "passageiros" : "motoristas"}`;
    default: return a.action;
  }
}
